import React, { useState } from 'react';
import photos from '../../images/Gallery/photos';
import TituloPagina from '../../js/Components/PageTitle';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRight,
  faCameraRetro,
  faChevronLeft,
  faChevronRight,
  faImages,
  faLocationDot,
  faShieldHeart,
  faTooth,
  faXmark
} from '@fortawesome/free-solid-svg-icons';

interface Photo {
  src: string;
  width: number;
  height: number;
  alt?: string;
}

const galleryPhotos: Photo[] = (photos as Photo[]).map((photo, index) => ({
  ...photo,
  alt: photo.alt || `Consultorio Dental Puente - foto ${index + 1}`,
}));

const PhotoGallery: React.FC = () => {
  const [activePhotoIndex, setActivePhotoIndex] = useState<number | null>(null);

  const closeLightbox = () => setActivePhotoIndex(null);

  const showPreviousPhoto = () => {
    setActivePhotoIndex((currentIndex) => {
      if (currentIndex === null) return null;
      return currentIndex === 0 ? galleryPhotos.length - 1 : currentIndex - 1;
    });
  };

  const showNextPhoto = () => {
    setActivePhotoIndex((currentIndex) => {
      if (currentIndex === null) return null;
      return currentIndex === galleryPhotos.length - 1 ? 0 : currentIndex + 1;
    });
  };

  const activePhoto = activePhotoIndex !== null ? galleryPhotos[activePhotoIndex] : null;

  return (
    <div>
      <TituloPagina title="DentalPuente | Galería" />

      <main className="gallery-page content-wrap">
        <section className="gallery-hero" aria-labelledby="gallery-title">
          <div className="gallery-hero-content">
            <span className="gallery-eyebrow">Galería Dental Puente</span>
            <h1 id="gallery-title">Un vistazo limpio y claro a nuestro consultorio.</h1>
            <p>
              Conoce el espacio donde atendemos a nuestros pacientes: áreas limpias, equipo preparado y una experiencia pensada para que llegues con tranquilidad.
            </p>
          </div>

          <aside className="gallery-hero-card" aria-label="Resumen de galería">
            <div className="gallery-hero-icon">
              <FontAwesomeIcon icon={faImages} />
            </div>
            <div>
              <span>Fotos disponibles</span>
              <strong>{galleryPhotos.length} imágenes</strong>
            </div>
          </aside>
        </section>

        <section className="gallery-feature-grid" aria-label="Puntos destacados">
          <article className="gallery-feature-card gallery-feature-card-primary">
            <FontAwesomeIcon icon={faTooth} />
            <div>
              <span>Consultorio</span>
              <h2>Atención dental profesional</h2>
              <p>Espacios diseñados para consulta, diagnóstico y seguimiento de pacientes.</p>
            </div>
          </article>

          <article className="gallery-feature-card">
            <FontAwesomeIcon icon={faShieldHeart} />
            <div>
              <span>Ambiente</span>
              <h2>Limpio y cómodo</h2>
              <p>Una vista rápida para que ubiques el lugar antes de tu visita.</p>
            </div>
          </article>

          <article className="gallery-feature-card">
            <FontAwesomeIcon icon={faLocationDot} />
            <div>
              <span>Ubicación</span>
              <h2>Guadalajara, Jalisco</h2>
              <p>Agenda por WhatsApp y confirma disponibilidad antes de asistir.</p>
            </div>
          </article>
        </section>

        <section className="gallery-showcase" aria-labelledby="gallery-showcase-title">
          <div className="gallery-section-header">
            <div>
              <span className="gallery-eyebrow">Recorrido visual</span>
              <h2 id="gallery-showcase-title">Fotos del consultorio</h2>
            </div>
            <span className="gallery-count-pill">
              <FontAwesomeIcon icon={faCameraRetro} />
              {galleryPhotos.length} fotos
            </span>
          </div>

          <div className="gallery-masonry-grid">
            {galleryPhotos.map((photo, index) => (
              <button
                type="button"
                key={`${photo.src}-${index}`}
                className={`gallery-photo-card gallery-photo-card-${(index % 5) + 1}`}
                onClick={() => setActivePhotoIndex(index)}
                aria-label={`Abrir foto ${index + 1} de la galería`}
              >
                <img src={photo.src} alt={photo.alt} loading="lazy" />
                <span className="gallery-photo-overlay">
                  <span>Ver foto</span>
                  <FontAwesomeIcon icon={faArrowRight} />
                </span>
              </button>
            ))}
          </div>
        </section>
      </main>

      {activePhoto && activePhotoIndex !== null && (
        <div className="gallery-lightbox" role="dialog" aria-modal="true" aria-label="Vista ampliada de la foto">
          <button type="button" className="gallery-lightbox-backdrop" onClick={closeLightbox} aria-label="Cerrar galería" />

          <div className="gallery-lightbox-panel">
            <div className="gallery-lightbox-toolbar">
              <span>Foto {activePhotoIndex + 1} de {galleryPhotos.length}</span>
              <button type="button" onClick={closeLightbox} aria-label="Cerrar imagen">
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>

            <button type="button" className="gallery-lightbox-arrow gallery-lightbox-arrow-left" onClick={showPreviousPhoto} aria-label="Foto anterior">
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>

            <img src={activePhoto.src} alt={activePhoto.alt} />

            <button type="button" className="gallery-lightbox-arrow gallery-lightbox-arrow-right" onClick={showNextPhoto} aria-label="Foto siguiente">
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PhotoGallery;
